import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { ThemeMode, ThemeColors } from '../assets/theme';
import { PaletteName, getPaletteColors } from '../design-system/themes';

interface ThemeContextType {
  mode: ThemeMode;
  palette: PaletteName;
  colors: ThemeColors;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
  setPalette: (palette: PaletteName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') {
      return saved;
    }
    // Check system preference
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      return 'dark';
    }
    return 'light';
  });

  const [palette, setPaletteState] = useState<PaletteName>(() => {
    const saved = localStorage.getItem('palette');
    if (
      saved === 'classic' ||
      saved === 'heritage' ||
      saved === 'mediterranean' ||
      saved === 'minimalist'
    ) {
      return saved;
    }
    return 'classic'; // Default to classic
  });

  useEffect(() => {
    localStorage.setItem('theme', mode);
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.style.colorScheme = mode;

    // Keep browser chrome (address bar / tab UI) in sync with the app theme toggle
    const themeColor = mode === 'dark' ? '#11161B' : '#3B3C80';
    const metas = document.querySelectorAll('meta[name="theme-color"]');
    if (metas.length === 0) {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = themeColor;
      document.head.appendChild(meta);
    } else {
      metas.forEach(meta => {
        meta.setAttribute('content', themeColor);
      });
    }
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('palette', palette);
    document.documentElement.setAttribute('data-palette', palette);
  }, [palette]);

  const toggleTheme = () => {
    setMode(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  const setPalette = (newPalette: PaletteName) => {
    setPaletteState(newPalette);
  };

  const colors = getPaletteColors(palette, mode);

  const value: ThemeContextType = {
    mode,
    palette,
    colors,
    toggleTheme,
    setTheme,
    setPalette,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
